import entries from 'lodash/entries';
import first from 'lodash/first';
import flatMap from 'lodash/flatMap';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import isArray from 'lodash/isArray';
import pickBy from 'lodash/pickBy';
import template from 'lodash/template';
import uniq from 'lodash/uniq';
import values from 'lodash/values';
import { getFieldNamesFromFormStructure } from '../operators';
import { isCheckboxGroup, reducei18n } from '../utils';
import { localizationKeyPath, requiredArg } from '../utils';

const KEYWORD_PRIORITIES = {
  required: 1,
  minLength: 2,
  maxLength: 3,
  format: 4,
  pattern: 5,
  enum: 6,
  type: 7,
  const: 8,
};

const defaultMessages = {
  errorMissing: "${label.replace(/\\?$/, '')} is required",
  errorInvalid: 'Please check ${ label.toLowerCase() }',
};

const getPathFromInstancePath = instancePath =>
  instancePath.slice(1).replace(/\//g, '.');

const getPropName = (array = [], separator = '.') =>
  array.filter(Boolean).join(separator);

export const removeDependentRequiredErrors = errors => {
  const requiredErrorProps = {};
  const schemaPathToFilter = [];

  errors.forEach(error => {
    const errorDataPath = getPathFromInstancePath(error.instancePath);

    switch (error.keyword) {
      case 'required':
        requiredErrorProps[
          getPropName([errorDataPath, error.params.missingProperty])
        ] = true;
        break;

      case 'if':
        if (get(error, 'schema.properties', false)) {
          Object.keys(error.schema.properties).forEach(prop => {
            if (requiredErrorProps[getPropName([errorDataPath, prop])]) {
              schemaPathToFilter.push(
                error.schemaPath.replace(/\/if$/, '/then')
              );
            }
          });
        }
        break;

      default:
        requiredErrorProps[errorDataPath] = true;
    }
  });

  return errors.filter(({ keyword, schemaPath }) => {
    if (keyword === 'required') {
      let match = false;

      schemaPathToFilter.forEach(schemaPathFilter => {
        if (schemaPath.search(schemaPathFilter) !== -1) {
          match = true;
        }
      });

      return !match;
    }

    return true;
  });
};

export const sortAndRemoveDuplicateErrors = errors =>
  flatMap(values(groupBy(errors, 'instancePath')), inputErrors => {
    const missingPropertyErrors = inputErrors.filter(error =>
      get(error, 'params.missingProperty', false)
    );

    const otherErrors = inputErrors.filter(
      error => !get(error, 'params.missingProperty', false)
    );

    if (otherErrors.length) {
      const otherErrorsByPriority = otherErrors.sort((a, b) =>
        (KEYWORD_PRIORITIES[a.keyword] || Infinity) <
        (KEYWORD_PRIORITIES[b.keyword] || Infinity)
          ? -1
          : 1
      );

      missingPropertyErrors.push(first(otherErrorsByPriority));
    }

    return missingPropertyErrors;
  });

export const getOneOfErrorPaths = (schema, parentPath) => {
  if (isArray(schema)) {
    const errorPaths = schema.reduce((acc, subSchema) => {
      const subSchemaErrorPaths = getOneOfErrorPaths(subSchema, parentPath);
      return [...acc, ...subSchemaErrorPaths];
    }, []);

    return uniq(errorPaths);
  }

  if (schema.properties) {
    return Object.keys(schema.properties).reduce((acc, propertyKey) => {
      const currentPath = `${parentPath}/${propertyKey}`;

      if (schema.properties[propertyKey].properties) {
        const subErrors = getOneOfErrorPaths(
          schema.properties[propertyKey],
          currentPath
        );

        return [...acc, ...subErrors];
      }

      return [...acc, currentPath];
    }, []);
  }

  return [];
};

export const getErrorsForPage = (pageName, errors) => {
  const prunedErrors = removeDependentRequiredErrors(errors);

  const sortedAndDeduplicatedErrors =
    sortAndRemoveDuplicateErrors(prunedErrors);

  return sortedAndDeduplicatedErrors
    .filter(({ instancePath, keyword }) => {
      // hide unwanted errors
      if (
        !instancePath.startsWith('/' + pageName) ||
        keyword === 'if' ||
        keyword === 'additionalProperties' ||
        keyword === 'contains'
      ) {
        return false;
      }

      return true;
    })
    .filter(({ keyword, parentSchema }) => {
      // do not hide anyOf error for checkbox group
      if (keyword === 'anyOf' && !isCheckboxGroup(parentSchema)) {
        return false;
      }

      return true;
    })
    .reduce((acc, error) => {
      // restructure oneOf errors
      const { instancePath, keyword, message, params, schema, schemaPath } =
        error;

      if (/oneOf\/[\d]+\//.test(schemaPath)) {
        // remove oneOf child errors
        return acc;
      }

      if (keyword === 'oneOf') {
        // custom error for each property in oneOf schema
        return [
          ...acc,
          ...getOneOfErrorPaths(schema, instancePath).map(path => ({
            instancePath: path,
            message, // reuse parent message
            keyword, // keyword is 'oneOf'
            params, // reuse parent params
            schemaPath,
          })),
        ];
      }

      return [...acc, error];
    }, []);
};

export const getFieldContext = (pathToFieldName, getMetadataForFieldName) => {
  let splitPathToFieldName = pathToFieldName.split('.');

  for (let i = 0; i < splitPathToFieldName.length - 1; i++) {
    let slicePathToFieldName = splitPathToFieldName.slice(
      1,
      splitPathToFieldName.length - i
    );

    let fieldName = [splitPathToFieldName[0], ...slicePathToFieldName].join(
      '.'
    );

    let fieldContext = getMetadataForFieldName(fieldName);

    if (fieldContext) return { fieldName, fieldContext };
  }

  return { fieldName: pathToFieldName, fieldContext: null };
};

export const getOrderedFieldNames = (
  getFormStructure,
  getSectionsForPageName,
  getSortOrderForFieldNames,
  pageName
) => {
  const pageSections = getSectionsForPageName(pageName);

  const sectionItems = flatMap(pageSections, section => section.items).map(
    item => `${pageName}.${item}`
  );

  const fieldNames = getFieldNamesFromFormStructure({
    [pageName]: getFormStructure()[pageName],
  });

  // sort by #section
  fieldNames.sort(
    (a, b) =>
      sectionItems.findIndex(item => a.startsWith(item)) -
      sectionItems.findIndex(item => b.startsWith(item))
  );

  const sortFields = entries(
    getSortOrderForFieldNames(pageName, context =>
      pickBy(context, (_, key) => key.startsWith(pageName))
    )
  ).reduce(
    (acc, field) => [
      ...acc,
      ...field[1].map(childField =>
        `${field[0]}.${childField}`.replace(/\.(\d+)\./g, '[$1].')
      ),
    ],
    []
  );

  // sort by #sort
  fieldNames.sort((a, b) => {
    const aIndex = sortFields.findIndex(item => a.startsWith(item));
    const bIndex = sortFields.findIndex(item => b.startsWith(item));

    if (aIndex >= 0 && bIndex >= 0) {
      return aIndex - bIndex;
    }

    return 0;
  });

  return fieldNames;
};

// loop through orderedFieldNames to lookup errors
export const sortErrorsByPropertyNames = (errors, orderedFieldNames) =>
  orderedFieldNames.reduce(
    (orderedErrors, fieldName) =>
      orderedErrors.concat(
        errors.filter(error => {
          // remove leading '.'
          const errorDataPath = getPathFromInstancePath(error.instancePath);
          const { missingProperty } = get(error, 'params', {});

          const errorFieldName = [errorDataPath, missingProperty]
            .filter(Boolean)
            .join('.')
            // remove trailing array index
            .replace(/^(.+)(\.\d+)$/g, '$1');

          return fieldName
            .replace(/\[/g, '.')
            .replace(/\]/g, '')
            .startsWith(errorFieldName);
        })
      ),
    []
  );

export const getFriendlyErrorMessages = ({
  errors = requiredArg('errors'),
  translations = requiredArg('translations'),
  values = requiredArg('values'),
  getMetadataForFieldName = requiredArg('getMetadataForFieldName'),
  getFormStructure = requiredArg('getFormStructure'),
  pageName = requiredArg('pageName'),
  getSectionsForPageName = requiredArg('getSectionsForPageName'),
  getSortOrderForFieldNames = requiredArg('getSortOrderForFieldNames'),
}) => {
  const orderedFieldNames = getOrderedFieldNames(
    getFormStructure,
    getSectionsForPageName,
    getSortOrderForFieldNames,
    pageName
  );

  errors = sortErrorsByPropertyNames(errors, orderedFieldNames);

  return errors.map(error => {
    const errorParams = get(error, 'params', {});
    const { missingProperty } = errorParams;
    const errorName = missingProperty ? 'errorMissing' : 'errorInvalid';
    const pathToProperty = getPathFromInstancePath(error.instancePath);

    const pathToFieldName = [pathToProperty, missingProperty]
      .filter(Boolean)
      .join('.');

    const { fieldContext, fieldName } = getFieldContext(
      pathToFieldName,
      getMetadataForFieldName
    );

    if (!fieldContext) {
      return { error, fieldName, message: error.message };
    }

    const localTranslations = get(
      translations,
      localizationKeyPath(get(fieldContext, ['context', 'keyPath']))
    );

    const errorMessage = (() => {
      const errorMessageTranslationPaths = [
        [localTranslations, [errorName + '.' + error.keyword]],
        [localTranslations, [errorName + '.' + '*']],
        [localTranslations, [errorName + '.' + error.keyword]],
        [localTranslations, [errorName + '.' + '*']],
        [translations, ['global', errorName + '.' + error.keyword]],
        [localTranslations, [errorName]],
        [translations, ['global', errorName]],
        [defaultMessages, [errorName]],
      ];

      for (const [translations, path] of errorMessageTranslationPaths) {
        const message = get(translations, path);

        if (message) {
          if (isArray(message)) {
            return first(reducei18n(message, values, fieldName)).source;
          }

          return message;
        }
      }

      return error.message;
    })();

    let formattedErrorMessage;

    try {
      formattedErrorMessage = template(errorMessage)({
        ...localTranslations,
        ...errorParams,
        value: get(values, fieldName),
      });
    } catch (err) {
      formattedErrorMessage = errorMessage;
    }

    return { error, fieldName, message: formattedErrorMessage };
  });
};
